import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Building2,
  CheckCircle2,
  Edit3,
  Plus,
  ShieldCheck,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { api, formatApiError } from "@/lib/api";

const CURRENCIES = ["INR", "RUB", "USD", "EUR"];

const EMPTY_FORM = {
  name: "",
  country: "",
  city: "",
  currency: "INR",
  duration_years: 6,
  tuition_year1: 0,
  tuition_year_n: 0,
  hostel_per_year: 0,
  food_per_year: 0,
  misc_per_year: 0,
  nmc_approved: true,
  image_url: "",
  notes: "",
  is_active: true,
};

export default function UniversitiesAdminPage() {
  const [universities, setUniversities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/universities");
      setUniversities(data.universities || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/admin/universities");
        if (!cancelled) setUniversities(data.universities || []);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  function openCreate() {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  }

  function openEdit(u) {
    setEditingId(u.id);
    setForm({
      name: u.name || "",
      country: u.country || "",
      city: u.city || "",
      currency: u.currency || "INR",
      duration_years: u.duration_years || 6,
      tuition_year1: u.tuition_year1 || 0,
      tuition_year_n: u.tuition_year_n || 0,
      hostel_per_year: u.hostel_per_year || 0,
      food_per_year: u.food_per_year || 0,
      misc_per_year: u.misc_per_year || 0,
      nmc_approved: u.nmc_approved ?? true,
      image_url: u.image_url || "",
      notes: u.notes || "",
      is_active: u.is_active ?? true,
    });
    setDialogOpen(true);
  }

  async function save() {
    if (!form.name.trim() || !form.country.trim() || !form.city.trim()) {
      toast.error("Name, Country and City are required");
      return;
    }
    try {
      const payload = {
        ...form,
        duration_years: parseInt(form.duration_years, 10) || 6,
        tuition_year1: parseFloat(form.tuition_year1) || 0,
        tuition_year_n: parseFloat(form.tuition_year_n) || 0,
        hostel_per_year: parseFloat(form.hostel_per_year) || 0,
        food_per_year: parseFloat(form.food_per_year) || 0,
        misc_per_year: parseFloat(form.misc_per_year) || 0,
      };
      if (editingId) {
        await api.patch(`/admin/universities/${editingId}`, payload);
        toast.success("University updated");
      } else {
        await api.post("/admin/universities", payload);
        toast.success("University added");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function remove(u) {
    if (!window.confirm(`Delete "${u.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/universities/${u.id}`);
      toast.success("University deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function seedDefaults() {
    if (
      !window.confirm(
        "Pre-load 12 default universities (Russia, Bulgaria, Moldova, Uzbekistan, Georgia)? Existing entries with the same name will be skipped."
      )
    )
      return;
    setSeeding(true);
    try {
      const { data } = await api.post("/admin/universities/seed-defaults");
      toast.success(`Added ${data.inserted} default universities`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSeeding(false);
    }
  }

  // Group by country
  const grouped = universities.reduce((acc, u) => {
    (acc[u.country] = acc[u.country] || []).push(u);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="universities-admin-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Universities</h1>
          <p className="text-sm text-slate-500 mt-1">
            Curate the list of universities shown in your Budget Calculator at{" "}
            <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">/budget-calculator</code>
          </p>
        </div>
        <div className="flex gap-2">
          {universities.length === 0 && (
            <Button
              variant="outline"
              onClick={seedDefaults}
              disabled={seeding}
              data-testid="seed-defaults-btn"
            >
              <Sparkles className="h-4 w-4 mr-1.5" />
              {seeding ? "Seeding…" : "Pre-load 12 defaults"}
            </Button>
          )}
          <Button
            onClick={openCreate}
            data-testid="add-university-btn"
            className="bg-[#0A2540] hover:bg-[#0A2540]/90"
          >
            <Plus className="h-4 w-4 mr-1.5" /> Add University
          </Button>
        </div>
      </header>

      {/* Stats */}
      <div className="grid sm:grid-cols-4 gap-3">
        <Stat label="Total" value={universities.length} />
        <Stat label="Active" value={universities.filter((u) => u.is_active).length} />
        <Stat label="Countries" value={Object.keys(grouped).length} />
        <Stat
          label="NMC Approved"
          value={universities.filter((u) => u.nmc_approved).length}
        />
      </div>

      {loading ? (
        <div className="text-center py-20 text-slate-500">Loading…</div>
      ) : universities.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-slate-200 bg-white p-10 text-center">
          <Building2 className="h-10 w-10 mx-auto text-slate-300" />
          <p className="text-sm text-slate-500 mt-3">
            No universities yet. Click <b>Pre-load 12 defaults</b> to bootstrap a curated list, or <b>Add University</b> to start from scratch.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([country, list]) => (
            <div key={country}>
              <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-2">
                {country} · {list.length}
              </div>
              <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                <table className="w-full text-sm" data-testid={`uni-table-${country}`}>
                  <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="text-left py-2 px-3">Name / City</th>
                      <th className="text-right py-2 px-3">Year 1</th>
                      <th className="text-right py-2 px-3">Year 2-6</th>
                      <th className="text-right py-2 px-3">Hostel/yr</th>
                      <th className="text-center py-2 px-3">Active</th>
                      <th className="text-right py-2 px-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((u) => (
                      <tr
                        key={u.id}
                        className="border-t border-slate-100 hover:bg-slate-50"
                        data-testid={`uni-row-${u.id}`}
                      >
                        <td className="py-2.5 px-3">
                          <div className="font-medium">
                            {u.name}
                            {u.nmc_approved && (
                              <ShieldCheck className="inline h-3.5 w-3.5 text-emerald-600 ml-1.5" />
                            )}
                          </div>
                          <div className="text-[11px] text-slate-500">
                            {u.city} · {u.currency} · {u.duration_years}yr
                          </div>
                        </td>
                        <td className="py-2.5 px-3 text-right tabular-nums">
                          {u.currency} {Intl.NumberFormat("en-IN").format(u.tuition_year1 || 0)}
                        </td>
                        <td className="py-2.5 px-3 text-right tabular-nums">
                          {u.currency} {Intl.NumberFormat("en-IN").format(u.tuition_year_n || 0)}
                        </td>
                        <td className="py-2.5 px-3 text-right tabular-nums">
                          {u.currency} {Intl.NumberFormat("en-IN").format(u.hostel_per_year || 0)}
                        </td>
                        <td className="py-2.5 px-3 text-center">
                          {u.is_active ? (
                            <CheckCircle2 className="h-4 w-4 text-emerald-600 mx-auto" />
                          ) : (
                            <X className="h-4 w-4 text-slate-300 mx-auto" />
                          )}
                        </td>
                        <td className="py-2.5 px-3 text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEdit(u)}
                            data-testid={`edit-uni-${u.id}`}
                          >
                            <Edit3 className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => remove(u)}
                            data-testid={`delete-uni-${u.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5 text-rose-600" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingId ? "Edit University" : "Add University"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3 mt-2">
            <Field label="University name *">
              <Input
                data-testid="uni-form-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Yaroslavl State Medical University"
              />
            </Field>
            <Field label="Country *">
              <Input
                data-testid="uni-form-country"
                value={form.country}
                onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
                placeholder="Russia"
              />
            </Field>
            <Field label="City *">
              <Input
                data-testid="uni-form-city"
                value={form.city}
                onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
                placeholder="Yaroslavl"
              />
            </Field>
            <Field label="Currency">
              <Select
                value={form.currency}
                onValueChange={(v) => setForm((f) => ({ ...f, currency: v }))}
              >
                <SelectTrigger data-testid="uni-form-currency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Duration (years)">
              <Input
                type="number"
                min={4}
                max={7}
                value={form.duration_years}
                onChange={(e) => setForm((f) => ({ ...f, duration_years: e.target.value }))}
              />
            </Field>
            <Field label="Tuition Year 1">
              <Input
                type="number"
                data-testid="uni-form-y1"
                value={form.tuition_year1}
                onChange={(e) => setForm((f) => ({ ...f, tuition_year1: e.target.value }))}
              />
            </Field>
            <Field label="Tuition Year 2 onwards">
              <Input
                type="number"
                data-testid="uni-form-yn"
                value={form.tuition_year_n}
                onChange={(e) => setForm((f) => ({ ...f, tuition_year_n: e.target.value }))}
              />
            </Field>
            <Field label="Hostel / year">
              <Input
                type="number"
                value={form.hostel_per_year}
                onChange={(e) => setForm((f) => ({ ...f, hostel_per_year: e.target.value }))}
              />
            </Field>
            <Field label="Food / year (optional)">
              <Input
                type="number"
                value={form.food_per_year}
                onChange={(e) => setForm((f) => ({ ...f, food_per_year: e.target.value }))}
              />
            </Field>
            <Field label="Misc / year (optional)">
              <Input
                type="number"
                value={form.misc_per_year}
                onChange={(e) => setForm((f) => ({ ...f, misc_per_year: e.target.value }))}
              />
            </Field>
            <Field label="Image URL">
              <Input
                value={form.image_url}
                onChange={(e) => setForm((f) => ({ ...f, image_url: e.target.value }))}
                placeholder="https://..."
              />
            </Field>
            <div className="sm:col-span-2">
              <Field label="One-line hook">
                <Input
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  placeholder="Established 1944. English-medium. WHO listed."
                />
              </Field>
            </div>
            <div className="sm:col-span-2 flex gap-6 mt-2">
              <label className="flex items-center gap-2 text-sm">
                <Switch
                  checked={form.nmc_approved}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, nmc_approved: v }))}
                />
                NMC Approved
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Switch
                  checked={form.is_active}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: v }))}
                />
                Active (visible to students)
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={save} data-testid="uni-form-save" className="bg-[#0A2540]">
              {editingId ? "Save changes" : "Add university"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-[0.14em] text-slate-500 mb-1">
        {label}
      </Label>
      {children}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
      <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500">
        {label}
      </div>
      <div className="text-2xl font-bold mt-0.5">{value}</div>
    </div>
  );
}
